import log from 'loglevel'

class MomiLoadTimeoutError extends Error {}

function waitForMomiToLoad(timeoutMs: number): Promise<void> {
  return new Promise((resolve, reject) => {
    // A flag to ensure promise is only resolved/rejected once.
    let isPending = true

    const resolveIfPending = () => {
      if (isPending) {
        isPending = false
        resolve()
      }
    }

    // First, try to listen for the event. This prepares for the case
    // where momi might load *just after* the initial check, but *before*
    // the listener is fully registered.
    window.addEventListener(
      'momi.loaded',
      (_event: Event) => {
        resolveIfPending()
      },
      { once: true } // Use { once: true } if you only need to call it once
    )

    // Then, immediately check if momi is already loaded.
    // This handles the case where momi loaded *before* waitForMomiToLoad was called.
    if ('momi' in window && typeof window['momi'] !== 'undefined') {
      resolveIfPending()
    } else {
      // Otherwise setup a timeout - it will reject the promise if not fulfilled
      // before timeout.
      setTimeout(() => {
        if (!isPending) {
          // Loaded before timeout.
          return
        }

        isPending = false
        reject(new MomiLoadTimeoutError())
      }, timeoutMs)
    }
  })
}

type WaitResult =
  | {
      status: 'loaded'
    }
  | {
      status: 'timeouted'
      timeoutMs: number
    }

export async function waitForClientSdkToLoadOrTimeout(timeoutMs: number): Promise<WaitResult> {
  log.debug(`Waiting for client SDK JS API to load for ${timeoutMs}ms`)
  const startTime = performance.now()

  try {
    await waitForMomiToLoad(timeoutMs)
  } catch (error: unknown) {
    if (error instanceof MomiLoadTimeoutError) {
      return { status: 'timeouted', timeoutMs }
    }
    throw error
  }

  const duration = performance.now() - startTime
  log.debug(`Client SDK JS API loaded in ${duration.toFixed(2)}ms`)
  return { status: 'loaded' }
}
